import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, User, LogOut, Settings, LayoutDashboard, Crown } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import './SiteChrome.css';

/**
 * Site navbar, extracted from the marketing-site UI kit
 * (docs/design-reference/uxauditpro-v1/ui_kits/marketing_site/index.html), where
 * it existed only as inline markup with no component boundary.
 *
 * CONTENT IS UNCHANGED. Every link target and every piece of anchor text is
 * carried over exactly as it was, including the two that look like mistakes and
 * are not this phase's to fix:
 *   - "Demo" points at /case-studies/ here and at /ux-auditor/ in the footer.
 * Pre-existing. Flagged, not silently corrected.
 *
 * The other one -- "Pricing" pointing at /ux-auditor/#pricing rather than a
 * /pricing route -- was FIXED on 2026-08-31, when /pricing became a real
 * prerendered page instead of an nginx 301 to that anchor.
 *
 * Styling is entirely in SiteChrome.css on the token layer. There are no
 * colour literals in this file, and no backdrop-filter: see the header comment
 * in that file for why the design's blurred translucent bar is not reproduced.
 */

const NAV_LINKS = [
  { to: '/ux-auditor/', label: 'UX Auditor', hash: null },
  { to: '/mvp-validator/', label: 'MVP Validator', hash: null },
  { to: '/use-cases/', label: 'Use Cases', hash: null },
  { to: '/compare/', label: 'Compare', hash: null },
  { to: '/pricing/', label: 'Pricing', hash: null },
  { to: '/case-studies/', label: 'Demo', hash: null },
];

const ADMIN_EMAIL = 'rinkesh.shah@roarsinc.com';

/* The kit's primary button: label, then a white disc holding the arrow. The
 * arrow is decorative — the label already names the action. */
const CtaArrow = () => (
  <span className="chrome-cta__disc" aria-hidden="true">
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h13M13 6l6 6-6 6" />
    </svg>
  </span>
);

const Brand = () => (
  <Link to="/" className="chrome-brand" data-testid="nav-logo">
    <span className="chrome-brand__mark" aria-hidden="true">UX</span>
    <span className="chrome-brand__word">UXAuditPro</span>
  </Link>
);

export const Navbar = () => {
  const { user, login, logout } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();

  const isAdmin = user?.email === ADMIN_EMAIL;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Hash scrolling when the page loads or the location changes.
  useEffect(() => {
    if (location.hash) {
      const elementId = location.hash.replace('#', '');
      setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location]);

  const handleNavClick = (e, link) => {
    if (link.hash) {
      e.preventDefault();
      if (location.pathname === link.to) {
        const element = document.getElementById(link.hash);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.location.href = `${link.to}#${link.hash}`;
      }
    }
  };

  return (
    <>
      {/* 4px spectrum bar. Decorative, carries no text, and the reason the nav
          sits at top:4px — it is the kit's chrome, not an invention. */}
      <div className="chrome-spectrum" aria-hidden="true" />

      <nav
        className={`chrome-nav${isScrolled ? ' chrome-nav--scrolled' : ''}`}
        data-site-chrome="nav"
        aria-label="Primary"
      >
        <div className="chrome-nav__inner">
          <Brand />

          <div className="chrome-nav__links">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={`${link.to}-${link.label}`}
                to={link.hash ? `${link.to}#${link.hash}` : link.to}
                onClick={(e) => handleNavClick(e, link)}
                className="chrome-navlink"
                end={!link.hash}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </NavLink>
            ))}
          </div>

          <div className="chrome-nav__actions">
            {user ? (
              <div style={{ position: 'relative' }}>
                <button
                  type="button"
                  className="chrome-avatarbtn"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  aria-expanded={userMenuOpen}
                  aria-haspopup="menu"
                  aria-label="Account menu"
                  data-testid="user-menu-btn"
                >
                  <span className="chrome-avatar">
                    {user.picture
                      ? <img src={user.picture} alt="" />
                      : <User size={16} aria-hidden="true" />}
                  </span>
                  <ChevronDown size={14} aria-hidden="true" />
                </button>

                {userMenuOpen && (
                  <>
                    <div className="chrome-menu__scrim" onClick={() => setUserMenuOpen(false)} />
                    <div className="chrome-menu" role="menu">
                      <div className="chrome-menu__head">
                        <p className="chrome-menu__name">{user.name}</p>
                        <p className="chrome-menu__email">{user.email}</p>
                      </div>
                      <Link to="/dashboard" role="menuitem" className="chrome-menu__item" onClick={() => setUserMenuOpen(false)} data-testid="nav-dashboard">
                        <LayoutDashboard size={16} aria-hidden="true" />
                        Dashboard
                      </Link>
                      <Link to="/account" role="menuitem" className="chrome-menu__item" onClick={() => setUserMenuOpen(false)} data-testid="nav-account">
                        <Settings size={16} aria-hidden="true" />
                        Account
                      </Link>
                      {isAdmin && (
                        <Link to="/admin" role="menuitem" className="chrome-menu__item chrome-menu__item--accent" onClick={() => setUserMenuOpen(false)} data-testid="nav-admin">
                          <Crown size={16} aria-hidden="true" />
                          Admin Panel
                        </Link>
                      )}
                      <div className="chrome-menu__sep">
                        <button type="button" role="menuitem" className="chrome-menu__item chrome-menu__item--danger" onClick={() => { logout(); setUserMenuOpen(false); }} data-testid="nav-logout">
                          <LogOut size={16} aria-hidden="true" />
                          Log out
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                <button type="button" className="chrome-textbtn" onClick={login} data-testid="nav-login">
                  Log in
                </button>
                <button type="button" className="chrome-cta" onClick={login} data-testid="nav-start-free">
                  Start free
                  <CtaArrow />
                </button>
              </>
            )}
          </div>

          <button
            type="button"
            className="chrome-burger"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-expanded={mobileMenuOpen}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="chrome-mobile">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={`mobile-${link.to}-${link.label}`}
                to={link.hash ? `${link.to}#${link.hash}` : link.to}
                onClick={(e) => { handleNavClick(e, link); setMobileMenuOpen(false); }}
                className="chrome-mobile__link"
              >
                {link.label}
              </NavLink>
            ))}
            {user ? (
              <div className="chrome-mobile__actions">
                <Link to="/dashboard" className="chrome-textbtn" onClick={() => setMobileMenuOpen(false)}>Dashboard</Link>
                <Link to="/account" className="chrome-textbtn" onClick={() => setMobileMenuOpen(false)}>Account</Link>
                <button type="button" className="chrome-textbtn chrome-textbtn--danger" onClick={() => { logout(); setMobileMenuOpen(false); }}>Log out</button>
              </div>
            ) : (
              <div className="chrome-mobile__actions">
                <button type="button" className="chrome-textbtn" onClick={login}>Log in</button>
                <button type="button" className="chrome-cta" onClick={login}>
                  Start free
                  <CtaArrow />
                </button>
              </div>
            )}
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;
